import {isNotNil, range} from 'ramda';

import type {Nullish} from 'shared/web';

const TICK_COUNT = 5;

// Recharts widens the step until the ticks fit. A few widenings always suffice.
const MAX_STEP_WIDENINGS = 20;

// What Recharts shows when every value is 0.
const EMPTY_AXIS_TICKS = [0, 1, 2, 3, 4];

interface ValueAxisTickOptions {
  /** False keeps every step a whole number, for values such as counts. */
  allowDecimals?: boolean;
}

// `toFixed` removes float noise such as 0.75 / 0.05 = 15.000000000000002 before rounding up.
function ceilRatio(value: number, step: number) {
  return Math.ceil(Number((value / step).toFixed(9)));
}

function getStep(roughStep: number, widening: number, allowDecimals: boolean) {
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const unit = roughStep >= 1 && roughStep < 10 ? magnitude : magnitude / 2;
  const step = (ceilRatio(roughStep, unit) + widening) * unit;
  return allowDecimals ? step : Math.ceil(step);
}

/**
 * Ticks of a value axis that includes 0, in the steps Recharts picks for its 5 default ticks:
 * the rough step rounded up to a half of its power of ten, or to a whole one below 10, widened
 * until 0 and the ticks below and above it fit. Knowing the ticks lets the chart size the axis
 * to its widest label.
 *
 * Returns `undefined` only if no step fits, and the axis then stays with Recharts.
 */
export function getValueAxisTicks(
  values: ReadonlyArray<number | Nullish>,
  options?: ValueAxisTickOptions
) {
  const numbers = values.filter(isNotNil);
  const min = Math.min(0, ...numbers);
  const max = Math.max(0, ...numbers);
  if (min === 0 && max === 0) {
    return EMPTY_AXIS_TICKS;
  }

  const roughStep = (max - min) / (TICK_COUNT - 1);
  for (let widening = 0; widening < MAX_STEP_WIDENINGS; widening += 1) {
    const step = getStep(roughStep, widening, options?.allowDecimals ?? true);
    const ticksBelowZero = ceilRatio(-min, step);
    const ticksAboveZero = ceilRatio(max, step);
    const spareTicks = TICK_COUNT - ticksBelowZero - ticksAboveZero - 1;
    if (spareTicks >= 0) {
      // Spare ticks go above 0, or below it when no value is positive.
      const firstTick = max > 0 ? -ticksBelowZero : -(ticksBelowZero + spareTicks);
      return range(0, TICK_COUNT).map((index) => (firstTick + index) * step);
    }
  }
  return undefined;
}
